import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

const EMPTY_FORM = {
  default_currency: '',
  default_country_code: '',
  default_origin_country: '',
  default_origin_city: '',
  default_origin_street: '',
  default_origin_house: '',
  default_origin_postal_code: '',
  timezone: '',
  webhook_secret: '',
  registration_url: '',
  delivery_options_text: '',
};

function emptyToNull(value) {
  const s = value == null ? '' : String(value).trim();
  return s === '' ? null : s;
}

function settingsToForm(data) {
  if (!data) return { ...EMPTY_FORM };
  const delivery = data.delivery_options;
  let delivery_options_text = '';
  if (delivery != null) {
    try {
      delivery_options_text = JSON.stringify(delivery, null, 2);
    } catch {
      delivery_options_text = String(delivery);
    }
  }
  return {
    default_currency: data.default_currency ?? '',
    default_country_code: data.default_country_code ?? '',
    default_origin_country: data.default_origin_country ?? '',
    default_origin_city: data.default_origin_city ?? '',
    default_origin_street: data.default_origin_street ?? '',
    default_origin_house: data.default_origin_house ?? '',
    default_origin_postal_code: data.default_origin_postal_code ?? '',
    timezone: data.timezone ?? '',
    webhook_secret: data.webhook_secret ?? '',
    registration_url: data.registration_url ?? '',
    delivery_options_text,
  };
}

function formToPayload(form, tenantId, isCreate) {
  let delivery_options = null;
  const rawDelivery = String(form.delivery_options_text || '').trim();
  if (rawDelivery) {
    try {
      delivery_options = JSON.parse(rawDelivery);
    } catch {
      throw new Error('invalidDeliveryOptionsJson');
    }
  }
  const payload = {
    default_currency: emptyToNull(form.default_currency),
    default_country_code: emptyToNull(form.default_country_code),
    default_origin_country: emptyToNull(form.default_origin_country),
    default_origin_city: emptyToNull(form.default_origin_city),
    default_origin_street: emptyToNull(form.default_origin_street),
    default_origin_house: emptyToNull(form.default_origin_house),
    default_origin_postal_code: emptyToNull(form.default_origin_postal_code),
    timezone: emptyToNull(form.timezone),
    webhook_secret: emptyToNull(form.webhook_secret),
    registration_url: emptyToNull(form.registration_url),
    delivery_options,
  };
  if (isCreate && tenantId) {
    payload.tenant_id = tenantId;
  }
  return payload;
}

function Field({ label, children, currentTheme }) {
  return (
    <label style={{ display: 'grid', gap: 0 }}>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: currentTheme.textSecondary }}>{label}</div>
      {children}
    </label>
  );
}

export default function TenantSettingsModal({ open, onClose, theme, currentTheme, tenantLabel, controlButtonStyle }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY_FORM);
  const [isCreate, setIsCreate] = useState(true);
  const [tenantId, setTenantId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);

  const resolveTenantId = useCallback(() => {
    const fromCompany =
      user?.myCompany?.tenant_id ??
      user?.myCompany?.tenantId ??
      user?.company?.tenant_id ??
      user?.company?.tenantId;
    return fromCompany != null && String(fromCompany).trim() !== '' ? String(fromCompany) : null;
  }, [user]);

  const loadSettings = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError(t('tenantSettingsMissingToken'));
      return;
    }
    const clientCoreApiUrl = process.env.REACT_APP_CLIENT_CORE_API_URL || '';
    const url = `${clientCoreApiUrl}/v1/clients/tenants/tenant-settings`;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setForm(settingsToForm(data));
        setTenantId(data?.tenant_id ?? resolveTenantId());
        setIsCreate(false);
        return;
      }
      if (res.status === 404) {
        setForm({ ...EMPTY_FORM });
        setTenantId(resolveTenantId());
        setIsCreate(true);
        return;
      }
      let message = t('tenantSettingsLoadFailed');
      try {
        const body = await res.json();
        if (body?.detail) {
          message = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail);
        }
      } catch {
        /* ignore */
      }
      setError(message);
    } catch (e) {
      console.error('Failed to load tenant settings:', e);
      setError(t('tenantSettingsLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [resolveTenantId, t]);

  useEffect(() => {
    if (!open) return;
    setShowWebhookSecret(false);
    loadSettings();
  }, [open, loadSettings]);

  const inputStyle = {
    width: '100%',
    boxSizing: 'border-box',
    borderRadius: 12,
    padding: '10px 12px',
    border: `1px solid ${currentTheme.border}`,
    background: currentTheme.background,
    color: currentTheme.text,
    outline: 'none',
  };

  const onSave = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError(t('tenantSettingsMissingToken'));
      return;
    }
    let payload;
    try {
      payload = formToPayload(form, tenantId, isCreate);
    } catch (e) {
      setError(t(e.message === 'invalidDeliveryOptionsJson' ? 'tenantSettingsInvalidJson' : 'tenantSettingsSaveFailed'));
      return;
    }
    if (isCreate && !payload.tenant_id) {
      setError(t('tenantSettingsMissingTenantId'));
      return;
    }

    const clientCoreApiUrl = process.env.REACT_APP_CLIENT_CORE_API_URL || '';
    const url = `${clientCoreApiUrl}/v1/clients/tenants/tenant-settings`;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(url, {
        method: isCreate ? 'POST' : 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const data = await res.json();
        setForm(settingsToForm(data));
        setTenantId(data?.tenant_id ?? tenantId);
        setIsCreate(false);
        return;
      }
      let message = t('tenantSettingsSaveFailed');
      try {
        const body = await res.json();
        if (body?.detail) {
          message = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail);
        }
      } catch {
        /* ignore */
      }
      setError(message);
    } catch (e) {
      console.error('Failed to save tenant settings:', e);
      setError(t('tenantSettingsSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <div
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: theme === 'light' ? 'rgba(17, 24, 39, 0.45)' : 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 2000,
      }}
    >
      <div
        style={{
          width: 'min(640px, 100%)',
          maxHeight: 'min(90vh, 820px)',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 16,
          background: currentTheme.surface,
          border: `1px solid ${currentTheme.border}`,
          boxShadow: currentTheme.cardShadow,
          color: currentTheme.text,
        }}
      >
        <div style={{ padding: '16px 16px 0', flexShrink: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 700 }}>
            {t('tenantSettingsTitle')}
            {tenantLabel ? ` — ${tenantLabel}` : ''}
          </div>
          {isCreate && !loading && (
            <div style={{ marginTop: 8, fontSize: 12, color: currentTheme.textSecondary }}>
              {t('tenantSettingsCreateHint')}
            </div>
          )}
        </div>

        <div style={{ padding: 16, overflowY: 'auto', flex: 1 }}>
          {loading && (
            <div style={{ color: currentTheme.textSecondary }}>{t('loading')}</div>
          )}

          {!loading && error && (
            <div
              style={{
                marginBottom: 12,
                padding: 12,
                borderRadius: 12,
                background: theme === 'light' ? '#FEF2F2' : 'rgba(239, 68, 68, 0.12)',
                border: `1px solid ${theme === 'light' ? '#FECACA' : 'rgba(239, 68, 68, 0.25)'}`,
              }}
            >
              {error}
            </div>
          )}

          {!loading && (
            <div style={{ display: 'grid', gap: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Field currentTheme={currentTheme} label={t('tenantSettingsDefaultCurrency')}>
                  <input value={form.default_currency} onChange={setField('default_currency')} style={inputStyle} />
                </Field>
                <Field currentTheme={currentTheme} label={t('tenantSettingsDefaultCountry')}>
                  <input value={form.default_country_code} onChange={setField('default_country_code')} style={inputStyle} />
                </Field>
              </div>

              <div style={{ fontSize: 13, fontWeight: 700, color: currentTheme.textSecondary }}>
                {t('tenantSettingsOrigin')}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Field currentTheme={currentTheme} label={t('tenantSettingsOriginCountry')}>
                  <input value={form.default_origin_country} onChange={setField('default_origin_country')} style={inputStyle} />
                </Field>
                <Field currentTheme={currentTheme} label={t('tenantSettingsOriginCity')}>
                  <input value={form.default_origin_city} onChange={setField('default_origin_city')} style={inputStyle} />
                </Field>
                <Field currentTheme={currentTheme} label={t('tenantSettingsOriginStreet')}>
                  <input value={form.default_origin_street} onChange={setField('default_origin_street')} style={inputStyle} />
                </Field>
                <Field currentTheme={currentTheme} label={t('tenantSettingsOriginHouse')}>
                  <input value={form.default_origin_house} onChange={setField('default_origin_house')} style={inputStyle} />
                </Field>
                <Field currentTheme={currentTheme} label={t('tenantSettingsOriginPostalCode')}>
                  <input
                    value={form.default_origin_postal_code}
                    onChange={setField('default_origin_postal_code')}
                    style={inputStyle}
                  />
                </Field>
                <Field currentTheme={currentTheme} label={t('tenantSettingsTimezone')}>
                  <input value={form.timezone} onChange={setField('timezone')} style={inputStyle} />
                </Field>
              </div>

              <Field currentTheme={currentTheme} label={t('tenantSettingsRegistrationUrl')}>
                <input value={form.registration_url} onChange={setField('registration_url')} style={inputStyle} />
              </Field>

              <Field currentTheme={currentTheme} label={t('Secret')}>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showWebhookSecret ? 'text' : 'password'}
                    value={form.webhook_secret}
                    onChange={setField('webhook_secret')}
                    style={{ ...inputStyle, paddingRight: 42 }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowWebhookSecret((v) => !v)}
                    aria-label={showWebhookSecret ? t('HideSecret') : t('ShowSecret')}
                    title={showWebhookSecret ? t('HideSecret') : t('ShowSecret')}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      right: 8,
                      transform: 'translateY(-50%)',
                      height: 28,
                      width: 28,
                      borderRadius: 8,
                      border: `1px solid ${currentTheme.border}`,
                      background: theme === 'light' ? '#FFFFFF' : 'rgba(255,255,255,0.04)',
                      color: currentTheme.textSecondary,
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  >
                    <span className="oi oi-eye" aria-hidden="true" />
                  </button>
                </div>
              </Field>

              <Field currentTheme={currentTheme} label={t('tenantSettingsDeliveryOptions')}>
                <textarea
                  value={form.delivery_options_text}
                  onChange={setField('delivery_options_text')}
                  rows={6}
                  placeholder="{}"
                  style={{ ...inputStyle, resize: 'vertical', fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
                />
              </Field>
            </div>
          )}
        </div>

        <div style={{ padding: 16, display: 'flex', gap: 10, justifyContent: 'flex-end', flexShrink: 0 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            style={{
              ...controlButtonStyle,
              background: 'transparent',
              color: currentTheme.text,
              border: `1px solid ${currentTheme.border}`,
            }}
          >
            {t('Cancel')}
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={loading || saving}
            style={{
              ...controlButtonStyle,
              opacity: loading || saving ? 0.6 : 1,
              cursor: loading || saving ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? t('Saving') + '...' : t('save')}
          </button>
        </div>
      </div>
    </div>
  );
}
